import styles from './TagsList.module.scss';
import colors from '../../../styles/_colors.module.scss';
import { useAddTagPopularityMutation, useGetTagsQuery } from '../../api';
import { FilterTag } from '../../ui';
import { DAY_URL_NAME, TOD_URL_NAME, useSessionId } from '../../filter';
import { ReactComponent as AllOptions } from '../../../assets/all-options.svg';
import { ReactComponent as Excursions } from '../../../assets/excursions.svg';
import { ReactComponent as Bridges } from '../../../assets/bridges.svg';
import { ReactComponent as Restaurants } from '../../../assets/restaurants.svg';
import { ReactComponent as Meteors } from '../../../assets/meteors.svg';
import { ReactComponent as Boats } from '../../../assets/boats.svg';
import { ReactComponent as Sailing } from '../../../assets/sailing.svg';
import { ReactComponent as Firework } from '../../../assets/firework.svg';
import { ReactComponent as Houseboats } from '../../../assets/houseboats.svg';
import { ReactComponent as Cruises } from '../../../assets/cruises.svg';
import { ReactComponent as CruisesMsk } from '../../../assets/cruises-msk.svg';
import { ReactComponent as BusTours } from '../../../assets/bus-tours.svg';
import { ReactComponent as Islands } from '../../../assets/islands.svg';
import { ReactComponent as OneDay } from '../../../assets/oneday.svg';
import { ReactComponent as TwoDays } from '../../../assets/twodays.svg';
import { ReactComponent as Mainland } from '../../../assets/mainland.svg';
import { ReactComponent as Active } from '../../../assets/active.svg';
import { ReactComponent as Private } from '../../../assets/private.svg';
import { ReactComponent as Premium } from '../../../assets/premium.svg';
import { ReactComponent as Motorboats } from '../../../assets/motorboats.svg';
import { ReactComponent as Dinners } from '../../../assets/dinners.svg';
import { ReactComponent as Parties } from '../../../assets/parties.svg';
import { ReactComponent as SightseeingDubai } from '../../../assets/sightseeing-db.svg';
import { ReactComponent as SightseeingPrague } from '../../../assets/sightseeing-pr.svg';
import { ReactComponent as Beer } from '../../../assets/beer.svg';
import { ReactComponent as Road } from '../../../assets/road.svg';
import { ReactComponent as Walking } from '../../../assets/walking.svg';
import { ReactComponent as Suburbs } from '../../../assets/suburbs.svg';
import { ReactComponent as Photographing } from '../../../assets/photographing.svg';
import { ReactComponent as SuburbsMoscow } from '../../../assets/suburbs-msk.svg';
import { ReactComponent as Winter } from '../../../assets/winter.svg';
import { ReactComponent as Radisson } from '../../../assets/radisson.svg';
import { ReactComponent as Now } from '../../../assets/now.svg';
import { ReactComponent as Presales } from '../../../assets/presales.svg';
import { ReactComponent as Rent } from '../../../assets/rent.svg';
import { ReactComponent as Top } from '../../../assets/top.svg';
import { ReactComponent as Rivers } from '../../../assets/rivers.svg';
import { ReactComponent as Petergof } from '../../../assets/petergof.svg';
import { ReactComponent as Music } from '../../../assets/music.svg';
import { ReactComponent as Kron } from '../../../assets/kron.svg';
import { ReactComponent as Gulf } from '../../../assets/gulf.svg';
import { ReactComponent as From } from '../../../assets/from.svg';
import { ReactComponent as Day } from '../../../assets/day.svg';
import { ReactComponent as Night } from '../../../assets/night.svg';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useAppSelector } from '../../../app';
import { FILTERS_KEY } from '../../trips';
import { TAG_URL_NAME } from '../';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { DateTime } from 'luxon';

interface TagsIcons {
  [tag: string]: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

interface TagsListProps {
  className?: string;
  isJustified?: boolean;
}

const TagsList = memo(function TagsList({
  className,
  isJustified,
}: TagsListProps): JSX.Element {
  const resource =
    process.env.REACT_APP_PATHNAME?.split('//')[1] || 'nevatrip.ru';
  const cityKey = useAppSelector(state => state.app.cityKey);
  const trips = useAppSelector(state => state.filter.trips);

  let sightseeingIcon;
  let partiesIcon;
  let thematicIcon;
  let cruisesIcon;
  let boatsrentIcon;

  if (resource.includes('busguide')) {
    sightseeingIcon = BusTours;
    partiesIcon = Beer;
    thematicIcon = Photographing;
    cruisesIcon = Cruises;
    boatsrentIcon = Boats;
  } else if (resource.includes('moskvatrip')) {
    sightseeingIcon =
      cityKey === 'dubai' ? SightseeingDubai : SightseeingPrague;
    partiesIcon = Parties;
    thematicIcon = Beer;
    cruisesIcon = CruisesMsk;
    boatsrentIcon = Private;
  } else {
    sightseeingIcon =
      cityKey === 'dubai' ? SightseeingDubai : SightseeingPrague;
    partiesIcon = Parties;
    thematicIcon = Beer;
    cruisesIcon = Cruises;
    boatsrentIcon = Boats;
  }

  const tagsIcons: TagsIcons = {
    excursions: Excursions,
    bridges: Bridges,
    restaurants: cityKey === 'stpb' ? Restaurants : Dinners,
    meteors: Meteors,
    boats: cityKey === 'stpb' ? Boats : Motorboats,
    sailing: Sailing,
    newyear: Firework,
    houseboats: Houseboats,
    cruises: cruisesIcon,
    bustours: BusTours,
    islands: Islands,
    oneday: OneDay,
    twodays: TwoDays,
    mainland: Mainland,
    sailboat: Sailing,
    active: Active,
    private: Private,
    boatsrent: boatsrentIcon,
    comfort: Boats,
    premium: Premium,
    motorboats: Motorboats,
    dinners: Dinners,
    parties: partiesIcon,
    sightseeing: sightseeingIcon,
    unique: Beer,
    weekend: Road,
    walking: Walking,
    suburbs: Suburbs,
    thematic: thematicIcon,
    holiday: Firework,
    suburbs_moscow: SuburbsMoscow,
    winter: Winter,
    radisson: Radisson,
    maynine: Firework,
    vmf: Firework,
    now: Now,
    presales: Presales,
    rent: Rent,
    top: Top,
    rivers: Rivers,
    petergof: Petergof,
    music: Music,
    kron: Kron,
    gulf: Gulf,
    from: From,
    day: Day,
    night: Night,
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTimesOfDay = searchParams.get(TOD_URL_NAME)?.split(',') ?? [];

  const selectedTag = searchParams.get(TAG_URL_NAME);
  const selectedDateStr = searchParams.get(DAY_URL_NAME);
  const selectedDateTimestamp = selectedDateStr
    ? DateTime.fromFormat(selectedDateStr, 'dd.MM.yyyy').toMillis()
    : 0;

  const { data: tagsData, isSuccess: isTagsSuccess } = useGetTagsQuery();

  const sesionId = useSessionId();
  const [addTagPopularity] = useAddTagPopularityMutation();
  const tagsWithAddedPopularity = useRef<string[]>([]);

  const availableTags = useMemo(() => {
    if (!isTagsSuccess || !trips) {
      return [];
    }

    let availableTags: string[] = tagsData.tags;

    if (
      (selectedTimesOfDay.length > 0 || selectedDateTimestamp > 0) &&
      trips.length > 0
    ) {
      availableTags = availableTags.filter(tag =>
        trips.some(trip => trip[FILTERS_KEY]?.includes(tag))
      );
    }

    return availableTags;
  }, [selectedTimesOfDay, selectedDateTimestamp, trips]);

  const handleOnChangeTag = useCallback(
    (tag: string) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (selectedTag === tag) {
        newSearchParams.delete(TAG_URL_NAME);
      } else {
        if (!tagsWithAddedPopularity.current.includes(tag)) {
          addTagPopularity({ session: sesionId, tag: tag });
          tagsWithAddedPopularity.current.push(tag);
        }
        newSearchParams.set(TAG_URL_NAME, tag);
      }

      setSearchParams(newSearchParams);
    },
    [searchParams]
  );

  if (isTagsSuccess) {
    return (
      <ul
        className={classNames(
          styles.root,
          isJustified && styles.justified,
          className,
          'TagsList'
        )}
      >
        <li className={styles.tag}>
          <FilterTag
            Icon={AllOptions}
            iconStroke={colors.black}
            tag='all_options'
            checked={selectedTag === null}
            onChange={() => {
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.delete(TAG_URL_NAME);

              setSearchParams(newSearchParams);
            }}
          />
        </li>
        {tagsData.tags.map(tag => {
          let tagIcon = tagsIcons[tag];

          if (!tagIcon) {
            tagIcon = AllOptions;
          }

          return (
            <li
              className={styles.tag}
              key={tag}
            >
              <FilterTag
                Icon={tagIcon}
                iconStroke={colors.black}
                tag={tag}
                disabled={!availableTags.includes(tag)}
                checked={selectedTag === tag}
                onChange={handleOnChangeTag}
              />
            </li>
          );
        })}
      </ul>
    );
  }

  return <></>;
});

export default TagsList;
